import { Pressable, Text, View, Keyboard, Platform, ActivityIndicator } from "react-native";
import usePlatform from "@/utils/hooks/usePlatform";
import SolidButton from "@/components/General/SolidButton";
import { redirectToDefault } from "@/utils/constants/linking";
import { useCallback, memo, useState, useEffect } from "react";
import { useRouter } from "expo-router";
import { Tooltip } from "@/components/General/Tooltip";
import Unicon from "@/components/General/Unicon";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import GuideTarget from "@/components/Guides/GuideTarget";

/**
 * A component that displays a modal header.
 * @param {object} props - The properties for the ModalHeader component.
 * @param {boolean} [props.loading=false] - Whether the modal is currently loading.
 * @param {boolean} [props.forSheet=false] - Whether the modal is for a sheet.
 * @param {string} props.title - The title of the modal.
 * @param {string} [props.confirmText="Save"] - The text for the confirm button.
 * @param {Function|null} [props.onConfirm=null] - A callback function that is called when the modal is confirmed.
 * @param {string} [props.cancelText="Cancel"] - The text for the cancel button.
 * @param {Function|null} [props.onCancel=null] - A callback function that is called when the modal is canceled.
 * @param {boolean} [props.goBackOnCancel=true] - Whether to go back on cancel.
 * @returns {JSX.Element} - The rendered modal header component.
 */
function ModalHeader({loading = false, forSheet = false, title, subtitle, centerTitle = false, confirmText = "Save", onConfirm = null, cancelText = "Cancel", onCancel = null, goBackOnCancel = true, helpText = "", headerRight = null, guideCancelTarget = null, confirmDisabled = false, confirmTestID = null}) {
    const { isPortrait, isLandscape, isWeb } = usePlatform();
    const { theme: { colors } } = useUnistyles();
    const [ enterHelpText, setEnterHelpText ] = useState("");
    const router = useRouter();
    const insets = useSafeAreaInsets();
    // const { enable, disable } = useLoader();

    // useEffect(() => {
    //     if(loading) {
    //         enable(true);
    //     } else {
    //         disable();
    //     }
    // }, [loading]);

    useEffect(() => {
        if(isWeb){
            if (navigator.platform.indexOf('Win') !== -1) {
                setEnterHelpText("ctrl + enter")
            } else if (navigator.platform.indexOf('Mac') !== -1) {
                setEnterHelpText("cmd + enter")
            }
        }
    }, [])

    const redirect = useCallback(() => {
        // No screen-name context here: defaultRedirects lookup misses, so this
        // resolves to canGoBack() ? back() : replace('/') — same as before.
        redirectToDefault(router);
    }, [router]);

    const handleCancel = useCallback(() => {
        if(onCancel) onCancel();
        if(goBackOnCancel) {
            redirect()
        }
    }, [onCancel, goBackOnCancel, redirect]);

    // Optional tour target for the cancel/close control. activate runs the
    // real cancel path so a hosted tour's tap performs the genuine action.
    const CancelWrap = guideCancelTarget ? GuideTarget : View;
    const cancelWrapProps = guideCancelTarget ? { id: guideCancelTarget, activate: handleCancel } : {};


    return (
        <View style={styles.container(forSheet, insets.top)}>
            {isPortrait &&
                <View style={styles.innerContainer}>
                    <CancelWrap {...cancelWrapProps}>
                    <Pressable hitSlop={10} onPress={handleCancel} style={({ pressed }) => styles.cancelButton(pressed)}>
                        {(!onCancel && !goBackOnCancel) ? <View></View> : <Text testID={title + "_ModalHeader_Cancel"} style={styles.cancelTextStyle}>{cancelText}</Text>}
                    </Pressable>
                    </CancelWrap>

                    <View style={styles.titleContainer}>
                        <View style={styles.titleColumn(centerTitle)}>
                            <Text style={styles.titleStyle(centerTitle)} numberOfLines={2}>{title}</Text>
                            {!!subtitle && <Text style={styles.subtitleStyle(centerTitle)} numberOfLines={1}>{subtitle}</Text>}
                        </View>
                        {!!helpText && <Tooltip title={helpText} appearOnTouch>
                            <Unicon name="help-circle-outline" size={20} colorVariant="textSecondary" />
                        </Tooltip>}
                    </View>

                    {headerRight}

                    { !onConfirm ? <View style={styles.spacer70}></View> :
                        <Pressable
                            hitSlop={10}
                            disabled={loading || confirmDisabled}
                            accessibilityState={{ disabled: loading || confirmDisabled }}
                            testID={confirmTestID || undefined}
                            style={({pressed}) => styles.confirmButton(pressed, loading || confirmDisabled)}
                            onPress={(e) => {
                                Keyboard.dismiss();
                                onConfirm(e);
                            }}
                        >
                            {loading
                                ? <ActivityIndicator size="small" color={colors.success} />
                                : <Text testID={confirmTestID ? undefined : title + "_ModalHeader_Confirm"} numberOfLines={1} style={styles.confirmTextStyle}>{confirmText}</Text>
                            }
                        </Pressable>
                    }
                </View>
            }


            {isLandscape &&
                <View style={styles.innerContainer}>
                    <View style={styles.landscapeTitleContainer}>
                        <View style={styles.titleColumn(centerTitle)}>
                            <Text style={styles.titleStyle(centerTitle)}>{title}</Text>
                            {!!subtitle && <Text style={styles.subtitleStyle(centerTitle)} numberOfLines={1}>{subtitle}</Text>}
                        </View>
                        {!!helpText && <Tooltip appearOnTouch title={helpText} relativeRender>
                            <Unicon name="help-circle-outline" size={22} colorVariant="textSecondary" />
                        </Tooltip>}
                    </View>

                    <View style={styles.landscapeActions}>
                        {headerRight}
                        {(onCancel || goBackOnCancel) && <CancelWrap {...cancelWrapProps}><SolidButton testID={title + "_ModalHeader_Cancel"} keyboardShortcutText="esc" size='sm' variant="muted" type="textSecondary" text={cancelText} onPress={handleCancel} loading={loading} disabled={loading} width={100} /></CancelWrap>}

                        {onConfirm && <SolidButton width={100} keyboardShortcutText={enterHelpText} testID={confirmTestID || (title + "ModalHeader_Confirm")} size='sm' type="success" text={confirmText} onPress={onConfirm} processing={loading} disabled={loading || confirmDisabled} />}

                    </View>
                </View>
            }
            
            {/* {!noLoader && <View style={{position:"absolute", bottom:-10}}><Progress.Bar progress={progress || 0} width={progress === null ? width*1.5 : width} borderWidth={0} borderRadius={0} height={3} color={colors.tertiary} indeterminateAnimationDuration={1200} indeterminate={!loading}/></View> } */}
        </View>
    )
}


const styles = StyleSheet.create(({colors, fonts}, rt) => ({
    container: (forSheet, safeAreaTop) => {
        const androidTop = Platform.OS === 'android' ? (safeAreaTop || 0) : 0;
        return {
            flexDirection:"row",
            alignItems:"center",
            justifyContent:rt.isLandscape ? "center" : "space-between",
            marginBottom:10,
            paddingHorizontal:rt.isLandscape ? (forSheet ? 0 : 25) : 20,
            paddingTop: 10 + androidTop,
            paddingBottom: rt.isLandscape ? 0 : 5,
            width:"100%",
            height: (rt.isLandscape ? 60 : 48) + androidTop
        };
    },
    innerContainer: {
        width:"100%",
        flexDirection:"row", 
        alignItems:"center", 
        justifyContent:"space-between"
    },
    confirmTextStyle: {
        fontFamily:fonts.heading, 
        color:colors.success, 
        fontSize:rt.isLandscape ? 16 : 15,
        textAlign:"center", 
        alignSelf:"flex-end"
    },
    cancelTextStyle: {
        fontFamily:fonts.body, 
        color:colors.textSecondary, 
        fontSize:rt.isLandscape ? 16 : 15, 
        textAlign:"center", 
        alignSelf:"flex-start"
    },
    titleStyle: (centerTitle) => ({
        fontFamily:fonts.heading, 
        color:colors.text, 
        fontSize:rt.isLandscape ? 20 : 16, 
        textAlign: rt.isLandscape && !centerTitle ? "left" : "center",
        alignSelf: "stretch",
    }),
    cancelButton: (pressed) => ({
        // minWidth (not fixed width) + no shrink: long cancel text must never
        // wrap/clip against the fixed-height header (mirrors confirmButton).
        minWidth: 70,
        flexShrink: 0,
        paddingHorizontal: 5,
        paddingVertical: 4,
        opacity: pressed ? 0.5 : 1
    }),
    titleContainer: {
        flex: 1,
        minWidth: 0,
        flexShrink: 1,
        flexDirection:"row",
        gap:5
    },
    titleColumn: () => ({
        flex: 1,
        minWidth: 0,
        alignItems: "stretch"
    }),
    subtitleStyle: (centerTitle) => ({
        fontFamily: fonts.body,
        color: colors.textSecondary,
        fontSize: 12,
        textAlign: rt.isLandscape && !centerTitle ? 'left' : 'center',
        marginTop: 2,
    }),
    spacer70: {
        width: 70
    },
    confirmButton: (pressed, loading) => ({
        // minWidth (not fixed width) + no shrink: long confirm text like
        // "Confirm (12)" must never wrap/clip against the fixed-height header.
        minWidth: 75,
        flexShrink: 0,
        paddingHorizontal: 5,
        paddingVertical: 4,
        opacity: pressed || loading ? 0.5 : 1
    }),
    landscapeTitleContainer: {
        flex: 1,
        minWidth: 0,
        flexShrink: 1,
        flexDirection:"row",
        alignItems:"center",
        gap:5
    },
    landscapeActions: {
        flexDirection:"row", 
        alignItems:"center", 
        gap:10, 
        justifyContent:"flex-end"
    }
}));

export default memo(ModalHeader)